import React, { useEffect } from "react";
import "./Documentupload.scss";
import { ProgressSpinner } from "primereact/progressspinner";
import {
  DocumentUploadViewModel,
  UploadedDocument,
} from "../viewmodel/Documentuploadviewmodel";

interface DocumentUploadProps {
  onChange: (fids: string[]) => void;
  initialDocuments?: UploadedDocument[]; // Pass full document objects
}

export const DocumentUpload: React.FC<DocumentUploadProps> = ({
  onChange,
  initialDocuments = [],
}) => {
  const {
    fileInputRef,
    uploadedDocuments,
    onUploadClick,
    onFileChange,
    isUploading,
    error,
    removeDocument,
    loadExistingDocuments,
  } = DocumentUploadViewModel();

  // Load existing documents when component mounts or initialDocuments changes
  useEffect(() => {
    if (initialDocuments.length > 0) {
      // console.log(
        // "DocumentUpload: Loading existing documents from props:",
        // initialDocuments,
      // );
      loadExistingDocuments(initialDocuments);
    }
  }, [initialDocuments, loadExistingDocuments]);

  // Notify parent when uploadedDocuments changes
  useEffect(() => {
    // No check for length > 0, so parent is notified of removals too
    const currentFids = uploadedDocuments.map((doc) => doc.fid);
    // console.log(
      // "DocumentUpload: Syncing FIDs to parent:",
      // currentFids,
      // uploadedDocuments,
    // );
    onChange(currentFids);
  }, [uploadedDocuments, onChange]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      await onFileChange(e);
      // Let the useEffect handle the parent notification
    } catch (err) {
      console.error("File upload failed:", err);
    }
  };

  const handleRemoveDocument = (fid: string) => {
    removeDocument(fid);
    // Let the useEffect handle the parent notification
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (filename: string): string => {
    if (filename.endsWith(".pdf")) return "pi-file-pdf";
    if (filename.endsWith(".doc") || filename.endsWith(".docx"))
      return "pi-file-word";
    return "pi-file";
  };

  return (
    <div className="document-upload">
      {/* Uploaded Documents List */}
      {/* {organizationData?.field_documents.length > 0 && (
        <div className="uploaded-documents-list mb-3">
          {organizationData.field_documents.map((doc: any) => (
            <div key={doc.fid} className="document-item">
              <div className="document-info">
                <i
                  className={`pi ${getFileIcon(doc.filename)} document-icon`}
                />
                <div className="document-details">
                  <span className="document-name">{doc.filename}</span>
                  <span className="document-size">
                    {formatFileSize(doc.filesize)}
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="btn-remove-document"
                onClick={() => handleRemoveDocument(doc.fid)}
                aria-label={`Remove ${doc.filename}`}
              >
                <i className="pi pi-times" />
              </button>
            </div>
          ))}
        </div>
      )} */}
      {uploadedDocuments.length > 0 && (
        <div className="uploaded-documents-list mb-3">
          {uploadedDocuments.map((doc) => (
            <div key={doc.fid} className="document-item">
              <div className="document-info">
                <i
                  className={`pi ${getFileIcon(doc.filename)} document-icon`}
                />
                <div className="document-details">
                  <span className="document-name">{doc.filename}</span>
                  <span className="document-size">
                    {formatFileSize(doc.filesize)}
                  </span>
                </div>
              </div>
              <button
                type="button"
                className="btn-remove-document"
                onClick={() => handleRemoveDocument(doc.fid)}
                aria-label={`Remove ${doc.filename}`}
              >
                <i className="pi pi-times" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload Area */}
      <div
        className={`upload-area ${isUploading ? "uploading" : ""}`}
        onClick={!isUploading ? onUploadClick : undefined}
        style={{ cursor: isUploading ? "not-allowed" : "pointer" }}
      >
        {isUploading ? (
          <div className="upload-loading">
            <ProgressSpinner
              style={{ width: "40px", height: "40px" }}
              strokeWidth="4"
            />
            <span className="upload-loading-text">Uploading...</span>
          </div>
        ) : (
          <>
            <div className="upload-icon">📁</div>
            <div className="upload-text">
              <span className="upload-link">Click to upload</span>
            </div>
            <p className="upload-hint">DOC, DOCX or PDF (max. 3MB)</p>
          </>
        )}
      </div>

      {/* Error Message */}
      {error && <p className="text-sm text-red-500 mt-2">{error}</p>}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        onChange={handleFileChange}
        style={{ display: "none" }}
        disabled={isUploading}
      />
    </div>
  );
};
